class SliderComponent extends HTMLElement {
    constructor() {
        super();
        this.rtl_num = document.body.classList.contains('layout_rtl') ? -1 : 1;
        this.scrollFrame = 0;
        this.boundSlider = null;
        this.controlBindings = new WeakSet();
        this.onScrollBound = this.onScroll.bind(this);
        this.onButtonClickBound = this.onButtonClick.bind(this);
        this.onDotClickBound = this.onDotClick.bind(this);
        this.onMutationBound = this.onMutation.bind(this);
    }

    connectedCallback() {
        this.refreshElements();
        this.bindEvents();
        this.initPages();
        this.afterRefresh();
    }

    disconnectedCallback() {
        if (this.boundSlider) {
            this.boundSlider.removeEventListener('scroll', this.onScrollBound);
        }
        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.edgeObserver?.disconnect();
        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    }

    getSlider() {
        return this.querySelector('ul');
    }

    getSliderItems() {
        return this.slider ? this.slider.querySelectorAll(':scope > li') : [];
    }

    refreshElements() {
        this.slider = this.getSlider();
        this.sliderItems = this.getSliderItems();
        this.pageCount = this.querySelector('.slider-counter--current');
        this.pageTotal = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.dotButton = this.querySelectorAll('button[name="dots"]');
    }

    bindEvents() {
        if (!this.slider) return;

        if (this.boundSlider !== this.slider) {
            this.boundSlider?.removeEventListener('scroll', this.onScrollBound);
            this.boundSlider = this.slider;
            this.boundSlider.addEventListener('scroll', this.onScrollBound, { passive: true });

            this.resizeObserver?.disconnect();
            if ('ResizeObserver' in window) {
                this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate(true));
                this.resizeObserver.observe(this.slider);
            }

            this.mutationObserver?.disconnect();
            this.mutationObserver = new MutationObserver(this.onMutationBound);
            this.mutationObserver.observe(this.slider, { childList: true, subtree: true });
        }

        [this.prevButton, this.nextButton].forEach((button) => {
            if (!button || this.controlBindings.has(button)) return;
            this.controlBindings.add(button);
            button.addEventListener('click', this.onButtonClickBound);
        });

        this.dotButton.forEach((button) => {
            if (this.controlBindings.has(button)) return;
            this.controlBindings.add(button);
            button.addEventListener('click', this.onDotClickBound);
        });
    }

    onMutation() {
        this.refreshElements();
        this.bindEvents();
        this.initPages();
        this.afterRefresh();
    }

    afterRefresh() {}

    scheduleUpdate(includePages = false) {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
            this.scrollFrame = 0;
            if (includePages) this.initPages();
            this.update();
        });
    }

    onScroll() {
        this.scheduleUpdate();
    }

    initPages() {
        const itemWidth = this.sliderItems?.[0]?.clientWidth || 0;
        if (!this.slider || !itemWidth) return;
        this.slidesPerPage = Math.max(1, Math.floor(this.slider.clientWidth / itemWidth));
        this.totalPages = Math.max(1, this.sliderItems.length - this.slidesPerPage + 1);
        if (this.pageCount) this.pageCount.textContent = this.slidesPerPage;
    }

    update() {
        const itemWidth = this.sliderItems?.[0]?.clientWidth || 0;
        if (!this.slider || !itemWidth) return;

        this.currentPage = Math.max(0, Math.round((this.slider.scrollLeft / itemWidth) * this.rtl_num));
        this.querySelector('.dots-item.active')?.classList.remove('active');
        this.querySelectorAll('.dots-item')[this.currentPage]?.classList.add('active');
        if (this.pageCount) this.pageCount.textContent = this.slidesPerPage + this.currentPage;
    }

    onButtonClick(event) {
        event.preventDefault();
        const itemWidth = this.sliderItems?.[0]?.clientWidth || 0;
        if (!this.slider || !itemWidth) return;

        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        this.slider.scrollTo({
            left: this.slider.scrollLeft + direction * itemWidth * this.rtl_num,
            behavior: 'smooth'
        });
    }

    onDotClick(event) {
        event.preventDefault();
        const itemWidth = this.sliderItems?.[0]?.clientWidth || 0;
        const targetIndex = Number.parseInt(event.currentTarget.dataset.index || '0', 10);
        if (!this.slider || !itemWidth || Number.isNaN(targetIndex)) return;

        this.querySelector('.dots-item.active')?.classList.remove('active');
        event.currentTarget.closest('.dots-item')?.classList.add('active');
        this.slider.scrollTo({
            left: targetIndex * itemWidth * this.rtl_num,
            behavior: 'smooth'
        });
    }
}

if (!customElements.get('slider-component')) {
    customElements.define('slider-component', SliderComponent);
}

class ProductSliderComponent extends SliderComponent {
    getSlider() {
        return this.querySelector('.slider');
    }

    getSliderItems() {
        if (!this.slider) return [];
        const slides = this.slider.querySelectorAll('.slider__slide');
        return slides.length ? slides : this.slider.querySelectorAll('div.product');
    }
}

class BannerSliderComponent extends SliderComponent {
    getSlider() {
        return this.querySelector('.slider');
    }

    getSliderItems() {
        return this.slider ? this.slider.querySelectorAll('.slider__slide') : [];
    }

    afterRefresh() {
        this.initButtonDisable();
    }

    initButtonDisable() {
        this.edgeObserver?.disconnect();
        if (!this.slider || !this.sliderItems.length || !('IntersectionObserver' in window)) return;

        const firstItem = this.sliderItems[0];
        const lastItem = this.sliderItems[this.sliderItems.length - 1];
        firstItem.classList.add('first-item');
        lastItem.classList.add('last-item');

        this.edgeObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target === firstItem) {
                    this.prevButton?.classList.toggle('disabled', entry.isIntersecting);
                }
                if (entry.target === lastItem) {
                    this.nextButton?.classList.toggle('disabled', entry.isIntersecting);
                }
            });
        }, { threshold: 0.8, root: this.slider });

        this.edgeObserver.observe(firstItem);
        if (lastItem !== firstItem) this.edgeObserver.observe(lastItem);
    }
}

class TiktokSliderComponent extends BannerSliderComponent {
    initPages() {
        const itemWidth = this.sliderItems?.[0]?.clientWidth || 0;
        if (!this.slider || !itemWidth) return;

        const distance = this.slider.clientWidth / itemWidth;
        this.slidesPerPage = distance % 1 > 0.5 ? Math.ceil(distance) : Math.floor(distance);
        this.totalPages = Math.max(1, Math.ceil(this.sliderItems.length - this.slidesPerPage) + 1);

        if (this.pageTotal && this.dataset.tiktokSlider === 'true') {
            this.pageTotal.textContent = this.totalPages;
        }

        const actions = this.querySelector('.slider-buttons');
        if (actions) {
            actions.style.display = this.totalPages <= 1 || window.innerWidth >= 1025 ? 'none' : 'flex';
        }
    }
}

const registerSliderElements = () => {
    if (!customElements.get('banner-slider-component')) {
        customElements.define('banner-slider-component', BannerSliderComponent);
    }
    if (!customElements.get('product-slider-component')) {
        customElements.define('product-slider-component', ProductSliderComponent);
    }
    if (!customElements.get('tiktok-slider-component')) {
        customElements.define('tiktok-slider-component', TiktokSliderComponent);
    }
};

if (document.readyState === 'complete') {
    registerSliderElements();
} else {
    window.addEventListener('load', registerSliderElements, { once: true });
}
